import React, { useState, useCallback, useEffect, useRef } from "react";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  VALIDATION_FIELD_MAPPING,
  APP_SUCCESS_MESSAGES,
  SOURCE,
  APP_ERROR_MESSAGES
} from "../../../../utils/appConstant";
import { formatDate, calculateNights } from "../../../../utils/methods"
import { Edit as EditInterface, EditApi } from "../../../../interfaces/reservation.interface";
import { Validation } from "../../../../interfaces/common";
import { Modules, NonAuthRoutes, RouteParamsTypes } from "../../../../enums";
import useFetch from "../../../../hooks/useFetch";
import ReservationService from "../../../../services/reservation.service";
import UserService from "../../../../services/user.service";
import { validateEditForm } from "../validations";
import { STATUS_CODE } from "../../../../utils/apiConstant";
import { TransactionStatus } from "../../../../interfaces/transaction.interface";
import Spinner from "../../../../components/Spinner";
import Form from "../components/Form";
import Stats from "../components/Stats";
import Buttons from "../components/Buttons";
import FormHeader from "../components/FormHeader";
import Toast from "../../../../components/Toast";
import Modal from '../../../../components/Modal';
import Button from "../../../../components/Button";
import useUnsavedChangesWarning from "../../../../hooks/useUnsavedChangesWarning";
import ConfirmationFormRouting from "../../../../components/ConfirmationFormRouting";
import ConfigurationModal from "../components/ConfigurationModal";
import CalculationPopover from "../components/CalculationPopover";
import CalculationDetailModal from "../components/CalculationDetailModal";

const initialFormData: EditInterface = {
  hospitableReservationId: '',
  invoiceNo: '',
  invoiceId: '',
  propertyId: '',
  property: '',
  city: '',
  reservationCode: '',
  platformId: '',
  platform: '',
  platformName: '',
  bookingDate: '',
  checkInDate: '',
  checkOutDate: '',
  nights: '',
  totalGuests: '',
  guestName: '',
  xNightRoomFees: '',
  perNightFees: '',
  cleaningFees: '',
  linenFees: '',
  petFees: '',
  hostFees: '',
  discount: '',
  matTax: '',
  hstTax: '',
  adjustments: '',
  hostPayout: '',
  actualRevenue: '',
  reservationStatusName: '',
  reservationStatus: '',
  status: '',
  statusName: '',
  statementStatus: '',
  statementStatusName: '',
  source: '',
  sourceName: '',
  ownerRevenue: '',
  commission: '',
  coHostCommission: '',
  clientRevenue: '',
  notes: '',
  reviewed: false,
  reviewedBy: "",
  isActive: true,
  managerStatement: null,
};

const initialValidation: Validation = { validate: true, fields: [] };

const initialDropdownSearchData = {
  reviewedBy: ""
};

const HOSPITABLE_SYNC_DATE_FIELDS: (keyof EditInterface)[] = [
  'bookingDate', 'checkInDate', 'checkOutDate',
];

const HOSPITABLE_SYNC_NUMERIC_FIELDS: (keyof EditInterface)[] = [
  'xNightRoomFees', 'discount', 'cleaningFees', 'linenFees', 'petFees',
  'hstTax', 'matTax', 'hostFees', 'adjustments', 'hostPayout',
];

interface UseEditViewControllerProps {
  paramId?: string;
  mode?: "view" | "edit";
  external?: boolean;
  onClose?: () => void;
}

export const useEditViewController = ({ paramId, mode, external, onClose }: UseEditViewControllerProps) => {
  const { t } = useTranslation();
  const params = useParams();
  const id = external ? paramId : params.id;
  const viewMode = !!useMatch(Modules.reservations + RouteParamsTypes.view + "/:id");
  const editMode = !!useMatch(Modules.reservations + RouteParamsTypes.edit + "/:id");
  const view = external ? (mode === "view") : viewMode;
  const edit = external ? (mode === "edit") : editMode;
  const navigate = useNavigate();

  const [formData, setFormData] = useState<EditInterface>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>(initialDropdownSearchData);
  const [confirmationId, setConfirmationId] = useState<string>('');
  const [configurationModalOpen, setConfigurationModalOpen] = useState(false);
  const [configurationLoading, setConfigurationLoading] = useState(false);
  const [accommodationPopoverAnchor, setAccommodationPopoverAnchor] = useState<HTMLElement | null>(null);
  const [guestFeesPopoverAnchor, setGuestFeesPopoverAnchor] = useState<HTMLElement | null>(null);
  const [calculationModalType, setCalculationModalType] = useState<'hostPayout' | 'ownerRevenue' | 'coHostCommission' | 'clientRevenue' | null>(null);

  const [shouldBlock, setShouldBlock] = useState(!view);
  const originalFormData = useRef<Partial<EditInterface>>({});

  // Unsaved changes warning
  const { showPrompt, confirmNavigation, cancelNavigation } = useUnsavedChangesWarning({
    when: shouldBlock,
  });

  const calculateStats = (
    netRevenue: string,
    taxes: string,
    propertyCommission: string,
    platformCommission: number | null | undefined,
    cleaningFees: string = '0',
    linenFees: string = '0',
    matTax: string = '0'
  ): { ownerRevenue: string, commission: string, coHostCommission: string, clientRevenue: string } => {
    const platformCommissionCleaning = platformCommission ? (Number(cleaningFees) || 0) * (Number(platformCommission) / 100) : 0;
    const platformCommissionLinen = platformCommission ? (Number(linenFees) || 0) * (Number(platformCommission) / 100) : 0;

    const netCleaningFee = platformCommission
      ? (Number(cleaningFees) || 0) - platformCommissionCleaning
      : (Number(cleaningFees) || 0);

    const netLinenFee = platformCommission
      ? (Number(linenFees) || 0) - platformCommissionLinen
      : (Number(linenFees) || 0);

    const hstTax = (Number(taxes) || 0) - (Number(matTax) || 0);

    const ownerRevenue = (
      (Number(netRevenue) || 0)
      - netCleaningFee
      - netLinenFee
      - hstTax
      - (Number(matTax) || 0)
    ).toFixed(2);

    const commission = (Number(propertyCommission) || 0).toFixed(2)
    const coHostCommission = ((Number(ownerRevenue) || 0) * ((Number(commission) || 0) / 100)).toFixed(2)
    const clientRevenue = ((Number(ownerRevenue) || 0) - (Number(coHostCommission) || 0)).toFixed(2)

    return { ownerRevenue, commission, coHostCommission, clientRevenue }
  }

  const { data: allReviewedByOptions } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: '', ...(!edit && !view && { is_active: '1' }) },
    dependency: [],
  });

  const { data: reviewedByOptions } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.reviewedBy, ...(!edit && !view && { is_active: '1' }), include: 'properties', include_id: formData.propertyId },
    dependency: [dropdownSearchData.reviewedBy, formData.propertyId],
  });

  useEffect(() => {
    setFormData(initialFormData)
    setDropdownSearchData(initialDropdownSearchData)
    setValidation(initialValidation)
    setShouldBlock(!view);
  }, [id, view]);

  const { loading: getContentLoading } = useFetch({
    service: ReservationService,
    func: "get",
    payload: id,
    dependency: [id, view],
    skip: !id,
    onSuccess: (response: any) => {
      if (response.status as TransactionStatus === VALIDATION_FIELD_MAPPING.settled && edit) {
        navigate(Modules.error + NonAuthRoutes.forbidden);
        return;
      }

      const platformCommission = response?.platform_commission ?? '0';
      const includeMatTax = response?.include_mat_tax ?? true;
      const includeAdjustments = response?.include_adjustments ?? false;

      const netRevenue = response?.revenue ?? "0";
      const taxes = ((Number(response?.mat_tax) || 0) + (Number(response?.taxes) || 0)).toFixed(2)

      const stats = calculateStats(
        netRevenue,
        taxes,
        response?.commission,
        platformCommission,
        response.cleaning_fee,
        response.linen_fee,
        response.mat_tax
      )

      const mappedFormData: Partial<EditInterface> = {
        bookingDate: response.booking_date ? formatDate(new Date(response.booking_date)) : '',
        checkInDate: formatDate(new Date(response.check_in_date)),
        checkOutDate: formatDate(new Date(response.check_out_date)),
        xNightRoomFees: response.accommodation_fees,
        discount: response.discount,
        cleaningFees: response.cleaning_fee,
        linenFees: response.linen_fee,
        petFees: response.pet_fee,
        hstTax: response.taxes,
        matTax: response.mat_tax,
        hostFees: response.host_fees,
        adjustments: response.adjustments,
        actualRevenue: response.actual_revenue,
        hostPayout: response.revenue ?? '0',
      };
      originalFormData.current = mappedFormData;

      setFormData({
        hospitableReservationId: response.hospitable_reservation_id,
        invoiceId: response?.invoice?.id ?? '',
        invoiceNo: response?.invoice?.invoice_no ?? '',
        property: response.property?.name,
        propertyId: response.property?.id,
        city: response?.property?.city_data?.name,
        reservationCode: response.reservation_code,
        platformId: response.platform?.id,
        platform: response.platform?.alias,
        platformName: response.platform?.platform_name,
        bookingDate: response.booking_date ? formatDate(new Date(response.booking_date)) : '',
        checkInDate: formatDate(new Date(response.check_in_date)),
        checkOutDate: formatDate(new Date(response.check_out_date)),
        nights: response.total_nights,
        totalGuests: response.total_guests,
        guestName: response.guest_name,
        xNightRoomFees: response.accommodation_fees,
        perNightFees: response.per_night_fees,
        cleaningFees: response.cleaning_fee,
        linenFees: response.linen_fee,
        petFees: response.pet_fee,
        hostFees: response.host_fees,
        discount: response.discount,
        matTax: response.mat_tax,
        hstTax: response.taxes,
        adjustments: response.adjustments,
        hostPayout: netRevenue,
        actualRevenue: response.actual_revenue,
        reservationStatus: response.reservation_status,
        reservationStatusName: response.reservation_status_name,
        status: response.status,
        statusName: response.status_name,
        statementStatus: response.statement_status,
        statementStatusName: response.statement_status_name,
        source: response.source,
        sourceName: response.source_name,
        notes: response.notes,
        reviewed: response.reviewed,
        reviewedBy: response?.reviewed_by_user?.id,
        platformCommission,
        includeMatTax,
        includeAdjustments,
        hstExpected: response.hst_expected ?? null,
        matTaxExpected: response.mat_tax_expected ?? null,
        matTaxPercentage: response.mat_tax_percentage ?? null,
        whoCollectsMat: response.who_collects_mat ?? null,
        isActive: response.is_active,
        managerStatement: response.manager_statement ? {
          id: response.manager_statement.id,
          statementNumber: response.manager_statement.statement_number,
          status: response.manager_statement.status,
        } : null,
        ...stats
      });
    },
    onError: (error: any) => {
      if (error.code === STATUS_CODE.notFound) navigate(Modules.reservations);
    }
  });


  const handleInputChange = useCallback((event: any, name: string) => {
    if (name === VALIDATION_FIELD_MAPPING.hostPayout) {
      setFormData((prev) => {
        const taxes = ((Number(prev.matTax) || 0) + (Number(prev.hstTax) || 0)).toFixed(2)

        return {
          ...prev,
          ...calculateStats(
            event.target.value,
            taxes,
            prev.commission || "",
            prev.platformCommission,
            prev.cleaningFees,
            prev.linenFees,
            prev.matTax
          ),
          [name]: event.target.value
        }
      });
    } else if ([
      VALIDATION_FIELD_MAPPING.xNightRoomFees, VALIDATION_FIELD_MAPPING.cleaningFees, VALIDATION_FIELD_MAPPING.linenFees,
      VALIDATION_FIELD_MAPPING.petFees, VALIDATION_FIELD_MAPPING.matTax, VALIDATION_FIELD_MAPPING.hstTax,
      VALIDATION_FIELD_MAPPING.hostFees, VALIDATION_FIELD_MAPPING.discount, VALIDATION_FIELD_MAPPING.adjustments,
    ].includes(name)) {
      setFormData((prev) => {
        const xNightRoomFees = (name === VALIDATION_FIELD_MAPPING.xNightRoomFees ? event.target.value : prev.xNightRoomFees);
        const cleaningFees = (name === VALIDATION_FIELD_MAPPING.cleaningFees ? event.target.value : prev.cleaningFees);
        const linenFees = (name === VALIDATION_FIELD_MAPPING.linenFees ? event.target.value : prev.linenFees);
        const petFees = (name === VALIDATION_FIELD_MAPPING.petFees ? event.target.value : prev.petFees);
        const matTax = (name === VALIDATION_FIELD_MAPPING.matTax ? event.target.value : prev.matTax);
        const hstTax = (name === VALIDATION_FIELD_MAPPING.hstTax ? event.target.value : prev.hstTax);
        const hostFees = (name === VALIDATION_FIELD_MAPPING.hostFees ? event.target.value : prev.hostFees);
        const discount = (name === VALIDATION_FIELD_MAPPING.discount ? event.target.value : prev.discount);
        const adjustments = (name === VALIDATION_FIELD_MAPPING.adjustments ? event.target.value : prev.adjustments);
        const includeAdjustments = prev.includeAdjustments ?? false;

        const hostPayout = (
          (Number(xNightRoomFees) || 0)
          - (Number(hostFees) || 0)
          - (Number(discount) || 0)
          + (Number(cleaningFees) || 0)
          + (Number(linenFees) || 0)
          + (Number(petFees) || 0)
          + (Number(matTax) || 0)
          + (Number(hstTax) || 0)
          + (includeAdjustments ? (Number(adjustments) || 0) : 0)
        ).toFixed(2);

        const taxes = ((Number(matTax) || 0) + (Number(hstTax) || 0)).toFixed(2)

        const totalNights = calculateNights(prev.checkInDate, prev.checkOutDate).toString();
        const perNightFees = Number(totalNights) ? (((Number(xNightRoomFees) || 0) - (Number(discount) || 0)) / Number(totalNights)).toFixed(2) : xNightRoomFees;

        return {
          ...prev,
          hostPayout,
          perNightFees,
          ...calculateStats(
            hostPayout,
            taxes,
            prev.commission || "",
            prev.platformCommission,
            cleaningFees,
            linenFees,
            matTax
          ),
          [name]: event.target.value
        }
      });
    } else if ([VALIDATION_FIELD_MAPPING.checkInDate, VALIDATION_FIELD_MAPPING.checkOutDate].includes(name)) {
      setFormData((prev) => {
        const checkInDate = (name === VALIDATION_FIELD_MAPPING.checkInDate ? event.target.value : prev.checkInDate);
        const checkOutDate = (name === VALIDATION_FIELD_MAPPING.checkOutDate ? event.target.value : prev.checkOutDate);
        const totalNights = calculateNights(checkInDate, checkOutDate).toString();
        const perNightFees = Number(totalNights) ? (((Number(prev.xNightRoomFees) || 0) - (Number(prev.discount) || 0)) / Number(totalNights)).toFixed(2) : prev.xNightRoomFees;

        return {
          ...prev,
          nights: totalNights,
          perNightFees,
          [name]: event.target.value
        }
      });
    } else {
      let value: any;
      if (name === VALIDATION_FIELD_MAPPING.reviewed || name === VALIDATION_FIELD_MAPPING.isActive) {
        value = !!event.target.checked;
      } else {
        value = event.target.value;
      }

      setFormData((prev) => {
        return {
          ...prev,
          [name]: value
        };
      });
    }
  }, []);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => {
      return {
        ...prev,
        [name]: event.target.value,
      };
    });
  }, [setDropdownSearchData]);

  const handleInputBlur = useCallback((event: any, name: string) => {
    let newValidation = validateEditForm({
      ...formData,
      [name]: event.target.value.trim(),
    }, name);
    setValidation(prev => {
      const filteredFields = prev.fields.filter(f => f.field !== name);
      const mergedFields = [...filteredFields, ...newValidation.fields];

      return {
        validate: mergedFields.length === 0,
        fields: mergedFields,
      };
    });
  }, [formData]);

  const handleSubmit = useCallback(async (event?: React.FormEvent, action?: 'no_confirmation') => {
    event && event.preventDefault();

    if ((Number(formData.hostPayout) || 0) < 0) {
      toast.error(<Toast message={APP_ERROR_MESSAGES.reservations.edit.invalidHostPayout} />, { icon: false });
      return;
    }

    const newValidation = validateEditForm(formData);
    setValidation(newValidation);
    if (!newValidation.validate) return;

    if (formData.source === SOURCE.hospitable && action !== 'no_confirmation') {
      const hasSyncFieldChanged =
        HOSPITABLE_SYNC_DATE_FIELDS.some(
          (field) => String(formData[field] ?? '') !== String(originalFormData.current[field] ?? '')
        ) ||
        HOSPITABLE_SYNC_NUMERIC_FIELDS.some(
          (field) => Number(formData[field] ?? 0) !== Number(originalFormData.current[field] ?? 0)
        );
      if (hasSyncFieldChanged) {
        handleConfirmation(id || "");
        return;
      }
    }

    setLoading(true);

    try {
      const payload: EditApi = {
        property_id: formData.propertyId,
        platform_id: formData.platformId,
        booking_date: formData.bookingDate ? formData.bookingDate + 'T00:00:00Z' : null,
        check_in_date: formData.checkInDate + 'T00:00:00Z',
        check_out_date: formData.checkOutDate + 'T00:00:00Z',
        total_nights: formData.nights,
        accommodation_fees: formData.xNightRoomFees,
        per_night_fees: formData.perNightFees,
        cleaning_fee: formData.cleaningFees,
        linen_fee: formData.linenFees,
        pet_fee: formData.petFees,
        host_fees: formData.hostFees,
        discount: formData.discount,
        mat_tax: formData.matTax,
        taxes: formData.hstTax,
        adjustments: formData.adjustments,
        revenue: formData.hostPayout,
        actual_revenue: formData.actualRevenue,
        notes: formData.notes,
        reviewed: formData.reviewed,
        reviewed_by: formData.reviewedBy,
        is_active: formData.isActive
      };

      let response: any = await ReservationService.edit(id || "", payload);

      if (response?.success !== false) {
        toast.success(<Toast message={APP_SUCCESS_MESSAGES.reservationUpdated} />, { icon: false });
        setConfirmationId('')

        setShouldBlock(false);
        setTimeout(() => {
          if (external) {
            onClose && onClose();
          } else {
            navigate(Modules.reservations);
          }
        }, 0);
      }

      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  }, [id, edit, formData, external, navigate, onClose]);

  const handleKeyDown = useCallback((e: any, name?: string) => {
    if (name && [VALIDATION_FIELD_MAPPING.expenseDetails, VALIDATION_FIELD_MAPPING.notes].includes(name)) return null
    if (e.key === 'Enter') handleSubmit();
  }, [handleSubmit]);

  const handleCancel = useCallback(() => {
    setShouldBlock(false);
    setTimeout(() => {
      if (external) {
        onClose && onClose();
      } else {
        navigate(Modules.reservations);
      }
    }, 0);
  }, [external, onClose])

  const handleConfirmation = useCallback((id: string) => {
    setConfirmationId(id)
  }, [formData])

  const handleConfigureClick = useCallback(() => {
    setConfigurationModalOpen(true);
  }, []);

  const handleConfigurationSave = useCallback(async (includeMatTax: boolean, includeAdjustments: boolean, platformCommission: number | null) => {
    if (!id) return;

    setConfigurationLoading(true);
    try {
      await ReservationService.updateSettings(id, {
        include_mat_tax: includeMatTax,
        include_adjustments: includeAdjustments,
        platform_commission: platformCommission
      });

      setFormData((prev) => {
        const hostPayout = (
          (Number(prev.xNightRoomFees) || 0)
          - (Number(prev.hostFees) || 0)
          - (Number(prev.discount) || 0)
          + (Number(prev.cleaningFees) || 0)
          + (Number(prev.linenFees) || 0)
          + (Number(prev.petFees) || 0)
          + (Number(prev.matTax) || 0)
          + (Number(prev.hstTax) || 0)
          + (includeAdjustments ? (Number(prev.adjustments) || 0) : 0)
        ).toFixed(2);

        const taxes = ((Number(prev.matTax) || 0) + (Number(prev.hstTax) || 0)).toFixed(2);

        return {
          ...prev,
          includeMatTax,
          includeAdjustments,
          platformCommission,
          hostPayout,
          ...calculateStats(
            hostPayout,
            taxes,
            prev.commission || "",
            platformCommission,
            prev.cleaningFees,
            prev.linenFees,
            prev.matTax
          )
        };
      });

      toast.success(<Toast message={APP_SUCCESS_MESSAGES.reservationUpdated} />, { icon: false });
      setConfigurationModalOpen(false);
    } catch (e) {
    } finally {
      setConfigurationLoading(false);
    }
  }, [id]);

  const handleAccommodationInfoClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAccommodationPopoverAnchor(event.currentTarget);
  }, []);

  const handleGuestFeesInfoClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setGuestFeesPopoverAnchor(event.currentTarget);
  }, []);

  let formContent;
  if (getContentLoading) {
    formContent = (
      <div className='card-header text-center'>
        <Spinner />
      </div>
    )
  } else {
    formContent = (
      <Form
        formData={formData}
        validation={validation}
        view={view}
        edit={edit}
        allReviewedByOptions={allReviewedByOptions}
        reviewedByOptions={reviewedByOptions}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onConfigureClick={handleConfigureClick}
        onAccommodationInfoClick={handleAccommodationInfoClick}
        onGuestFeesInfoClick={handleGuestFeesInfoClick}
      />
    )
  }

  const modalAction = (
    <>
      <Button
        className="btn btn-outline-secondary me-3"
        text={t("reservations.edit.pageTexts.form.confirmModal.cancelBtn")}
        onClick={e => setConfirmationId('')}
      />
      <Button
        className="btn btn-primary me-3"
        text={t("reservations.edit.pageTexts.form.confirmModal.yesBtn")}
        loading={loading}
        onClick={e => handleSubmit(e, 'no_confirmation')}
      />
    </>
  )

  const content = (
    <>
      <div className="row align-items-start">
        <div className="col-xl-9 col-md-8 col-12 d-flex flex-column">
          <div className="card">
            {!external && <FormHeader formData={formData} view={view} />}

            {!external && <hr className="m-0" />}

            {formContent}

            <hr className="m-0" />

            {!external &&
              <Buttons
                loading={loading}
                view={view}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
              />
            }
          </div>
        </div >
        <Stats
          formData={formData}
          external={!!external}
          onHostPayoutClick={() => setCalculationModalType('hostPayout')}
          onOwnerRevenueClick={() => setCalculationModalType('ownerRevenue')}
          onCoHostCommissionClick={() => setCalculationModalType('coHostCommission')}
          onClientRevenueClick={() => setCalculationModalType('clientRevenue')}
        />
      </div>
      <Modal
        open={confirmationId ? true : false}
        onClose={() => setConfirmationId('')}
        title={t("reservations.edit.pageTexts.form.confirmModal.title")}
        content={t("reservations.edit.pageTexts.form.confirmModal.content")}
        action={modalAction}
        contentClassName="text-center"
        maxWidth="sm"
      />
      <ConfirmationFormRouting
        open={showPrompt}
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
      />
      <ConfigurationModal
        open={configurationModalOpen}
        onClose={() => setConfigurationModalOpen(false)}
        onSave={handleConfigurationSave}
        initialIncludeMatTax={formData.includeMatTax ?? true}
        initialIncludeAdjustments={formData.includeAdjustments ?? false}
        initialPlatformCommission={formData.platformCommission}
        loading={configurationLoading}
        view={view}
      />
      <CalculationPopover
        open={!!accommodationPopoverAnchor}
        anchorEl={accommodationPopoverAnchor}
        onClose={() => setAccommodationPopoverAnchor(null)}
        formData={formData}
        type="accommodation"
      />
      <CalculationPopover
        open={!!guestFeesPopoverAnchor}
        anchorEl={guestFeesPopoverAnchor}
        onClose={() => setGuestFeesPopoverAnchor(null)}
        formData={formData}
        type="guestFees"
      />
      {calculationModalType && (
        <CalculationDetailModal
          open={!!calculationModalType}
          onClose={() => setCalculationModalType(null)}
          formData={formData}
          type={calculationModalType}
        />
      )}
    </>
  )

  return {
    formData,
    loading,
    validation,
    dropdownSearchData,
    confirmationId,
    allReviewedByOptions,
    reviewedByOptions,
    getContentLoading,
    view,
    edit,
    id,
    content,
    external,
    setFormData,
    setValidation,
    setDropdownSearchData,
    handleInputChange,
    handleDropdownSearchInputChange,
    setConfirmationId,
    handleInputBlur,
    handleSubmit,
    handleKeyDown,
    handleCancel,
    handleConfirmation,
    calculateStats,
  };
};
